/**
 * AIAssistStatusBanner — renders the AI-assist outcome on a workflow
 * report.
 *
 * Three states surface to the user:
 *   - "off"      : not shown (deterministic-only path, no banner needed)
 *   - "ok"       : explicit "AI-assisted draft, still requires human
 *                  review" banner is rendered (this is intentionally
 *                  the same banner as the toggle's pre-submit warning)
 *   - "fallback" : neutral notice that the gateway was unavailable and
 *                  the deterministic body is the source of truth
 *
 * The component never displays prompt bodies or raw model output —
 * only safe metadata (task, prompt-hash short, latency, cost).
 */

import type { JSX } from "react";

import type {
  AIAssistStatus,
  AIAuditEnvelope,
} from "../api/client";

interface Props {
  status: AIAssistStatus | undefined;
  banner: string;
  fallbackText: string;
  offText: string;
  auditHeader: string;
  audit: AIAuditEnvelope | null | undefined;
}

export function AIAssistStatusBanner({
  status,
  banner,
  fallbackText,
  offText,
  auditHeader,
  audit,
}: Props): JSX.Element | null {
  if (!status || status === "off") {
    return (
      <div
        className="po-note"
        data-testid="po-ai-assist-status-off"
      >
        {offText}
      </div>
    );
  }
  if (status === "fallback") {
    return (
      <div
        className="po-banner po-banner--warn"
        role="status"
        data-testid="po-ai-assist-status-fallback"
      >
        {fallbackText}
      </div>
    );
  }
  return (
    <div
      className="po-banner po-banner--warn"
      role="status"
      data-testid="po-ai-assist-status-ok"
    >
      <p>{banner}</p>
      {audit ? (
        <details>
          <summary>{auditHeader}</summary>
          <ul className="po-ai-assist-audit">
            <li>
              <code>task_type</code>: {audit.task_type}
            </li>
            <li>
              <code>prompt_handle</code>: {audit.prompt_handle}
            </li>
            <li>
              <code>prompt_hash_short</code>: {audit.prompt_hash_short}
            </li>
            <li>
              <code>latency_ms</code>: {audit.latency_ms}
            </li>
            <li>
              <code>cost_usd</code>: {audit.cost_usd.toFixed(4)}
            </li>
          </ul>
        </details>
      ) : null}
    </div>
  );
}

export default AIAssistStatusBanner;
